/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { ArrowLeft } from 'lucide-react'
import { cn } from '@/lib/utils'

type BackBtnProps = {
  label?: string
  fallback?: string
  showShortcut?: boolean
  sticky?: boolean
  to?: string
  className?: string
  variant?:
    | 'default'
    | 'secondary'
    | 'outline'
    | 'ghost'
    | 'destructive'
    | 'link'
  size?: 'default' | 'sm' | 'lg' | 'icon'
}

export default function BackBtn({
  label = 'Retour',
  fallback = '/',
  showShortcut = true,
  sticky = false,
  to,
  className,
  variant = 'outline',
  size = 'default',
}: BackBtnProps) {
  const navigate = useNavigate()
  const location = useLocation()

  const goBack = () => {
    if (to) {
      navigate(to)
      return
    }

    // 1) Si on a un "from" (list → détail), on y retourne directement
    const from = (location.state as any)?.from
    if (from) {
      navigate(from)
      return
    }

    // 2) Sinon, on vérifie l’index du stack géré par React Router
    const canGoBack =
      typeof window !== 'undefined' && window.history.state?.idx > 0
    if (canGoBack) {
      navigate(-1)
    } else {
      // 3) Sinon, on va vers un fallback propre
      navigate(fallback, { replace: true })
    }
  }

  useEffect(() => {
    if (!showShortcut) return
    const handler = (e: KeyboardEvent) => {
      const isMac =
        typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform)
      const macCombo = isMac && e.metaKey && e.key === '['
      const winCombo = !isMac && e.altKey && e.key === 'ArrowLeft'
      if (macCombo || winCombo) {
        e.preventDefault()
        goBack()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [showShortcut, location.key, goBack]) // rebind si la page change

  const isIconOnly = size === 'icon'
  const isMac =
    typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform)
  const shortcutLabel = isMac ? '⌘ [' : 'Alt ←'

  const btn = (
    <Button
      type='button'
      variant={variant}
      size={size}
      onClick={goBack}
      aria-label={label}
      className={cn(
        'gap-2 rounded-2xl transition-all',
        isIconOnly ? 'p-2' : 'pl-2 pr-3',
        sticky &&
          'fixed top-4 left-4 z-40 backdrop-blur supports-[backdrop-filter]:bg-background/60',
        className
      )}
    >
      <ArrowLeft className='h-4 w-4' />
      {!isIconOnly && <span className='font-medium'>{label}</span>}
      {!isIconOnly && showShortcut && (
        <kbd className='ml-1 text-[10px] rounded border bg-muted px-1 py-0.5'>
          {shortcutLabel}
        </kbd>
      )}
    </Button>
  )

  return (
    <Tooltip delayDuration={150}>
      <TooltipTrigger asChild>{btn}</TooltipTrigger>
      <TooltipContent side='bottom'>
        Revenir à la page précédente
      </TooltipContent>
    </Tooltip>
  )
}
